import React, { useState, type FC } from 'react';

import styled from 'styled-components';

import { Flex, Text } from '@adsk/alloy-react-box';
import { IconButton } from '@adsk/alloy-react-button';
import {
  XIcon,
  MoreHorizontalIcon,
  DownloadIcon,
  TrashCanIcon,
} from '@adsk/alloy-react-icon';
import Menu from '@adsk/alloy-react-menu';
import Modal, { FullScreenModal } from '@adsk/alloy-react-modal';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import { DocsFileTypes } from '@adsk/docs-js-sdk';

import AttachmentsViewerDetails from './AttachmentsViewerDetails';
import AttachmentsViewerToolbar from './AttachmentsViewerToolbar';
import { useTranslation } from '../../i18n';
import AttachmentItemIcon from '../AttachmentItem/AttachmentItemIcon';

type AttachmentsViewerProps = {
  filename: string;
  open: boolean;
  onClose: () => void;
  onDownload: () => void;
  onDelete: () => void;
  createdOn: string;
  fileSize: number;
  /**
   * @deprecated
   * This prop is deprecated and will be removed in the future. Use createdBy instead.
   */
  createdByName?: string;
  /**
   * @info
   * CreatedBy gets oxygenId.
   */
  createdBy?: string;
  onNext: () => void;
  onPrevious: () => void;
  currentIndex: number;
  totalAttachments: number;
  zoomIn: () => void;
  zoomOut: () => void;
  currentZoomLevel: number;
  preview: string | undefined;
  loading: boolean;
  type?: DocsFileTypes;
  allowDelete?: boolean;
};

const footerPlaceholder = '160px';

const AttachmentsViewerActionOption = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const AttachmentsViewer: FC<AttachmentsViewerProps> = ({
  filename,
  open,
  onClose,
  onDownload,
  onDelete,
  createdOn,
  fileSize,
  createdByName,
  createdBy,
  onNext,
  onPrevious,
  currentIndex,
  totalAttachments,
  currentZoomLevel,
  zoomIn,
  zoomOut,
  preview,
  loading,
  type,
  allowDelete = true,
}) => {
  const { t } = useTranslation();
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  const isImage = type === 'image';

  return (
    <FullScreenModal open={open}>
      <Modal.Header style={{ alignItems: 'center' }}>
        <Flex data-testid="AttachmentsViewerHeader" flexShrink={1} flexGrow={1}>
          <OverflowTooltip>{filename}</OverflowTooltip>
        </Flex>
        <Flex
          alignItems="center"
          flexShrink={0}
          justifyContent="space-between"
          style={{ gap: theme.spacing.xSmall }}
        >
          <IconButton>
            <MoreHorizontalIcon
              data-testid="AttachmentsViewerMenu"
              onClick={() => setIsActionsOpen((prev) => !prev)}
            />
            {isActionsOpen && (
              <Menu
                style={{
                  position: 'absolute',
                  top: '48px',
                  right: '48px',
                  left: 'unset',
                  width: '180px',
                }}
                options={[
                  {
                    key: 'AttachmentsViewerDownload',
                    label: (
                      <AttachmentsViewerActionOption>
                        <DownloadIcon />
                        <span>{t('DOWNLOAD')}</span>
                      </AttachmentsViewerActionOption>
                    ),
                    onClick: () => {
                      onDownload();
                      setIsActionsOpen(false);
                    },
                  },
                  {
                    key: 'AttachmentsViewerDelete',
                    label: (
                      <AttachmentsViewerActionOption>
                        <TrashCanIcon />
                        <span>{t('DELETE')}</span>
                      </AttachmentsViewerActionOption>
                    ),
                    onClick: () => {
                      onDelete();
                      setIsActionsOpen(false);
                    },
                    disabled: !allowDelete,
                  },
                ]}
              />
            )}
          </IconButton>
          <IconButton data-testid="AttachmentsViewerClose" onClick={onClose}>
            <XIcon />
          </IconButton>
        </Flex>
      </Modal.Header>
      <Flex height="100%">
        <Flex
          position="relative"
          flex={1}
          height="100%"
          backgroundColor={theme.colors.charcoal050}
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
        >
          {!loading && preview && type === 'image' && (
            <img
              data-testid="AttachmentsViewerImage"
              src={preview}
              alt=""
              width="auto"
              height="auto"
              style={{
                maxWidth: '100%',
                maxHeight: `calc(100% - ${footerPlaceholder})`,
                scale: currentZoomLevel + '%',
              }}
            />
          )}
          {!loading && preview && type === 'video' && (
            <video
              data-testid="AttachmentsViewerVideo"
              controls
              src={preview}
              width="auto"
              height="auto"
              style={{
                maxWidth: '100%',
                maxHeight: `calc(100% - ${footerPlaceholder})`,
                alignSelf: 'start',
              }}
            />
          )}
          {loading && <ProgressRing data-testid="AttachmentsViewerLoader" />}
          {!loading && !preview && (
            <Flex
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <AttachmentItemIcon
                color={theme.colors.charcoal500}
                name={filename}
                style={{
                  marginBottom: theme.spacing.xSmall,
                }}
              />
              <Text
                typography="bodyMedium"
                style={{ color: theme.colors.charcoal500 }}
              >
                {t('PREVIEW_UNAVAILABLE')}
              </Text>
            </Flex>
          )}
          <AttachmentsViewerToolbar
            onNext={onNext}
            onPrevious={onPrevious}
            currentIndex={currentIndex}
            totalAttachments={totalAttachments}
            currentZoomLevel={currentZoomLevel}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            allowZoom={isImage}
          />
        </Flex>
        <AttachmentsViewerDetails
          createdOn={createdOn}
          fileSize={fileSize}
          createdBy={createdBy}
          createdByName={createdByName}
        />
      </Flex>
    </FullScreenModal>
  );
};

export default AttachmentsViewer;
